import { KeyValuePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '@environments/environment';
import { ExtensionService, TExtension } from '@shared/services/extension.service';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { IProjectSettings } from '@static/models/project';
import { HttpService } from '@static/services/http.service';
import { SnackService } from '@static/services/snack.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { NewProjectIdComponent } from '../../../shared/dialogs/new-project-id/new-project-id.component';
import { AuthService } from '@static/services/auth.service';
import moment from 'moment';
import { ICompanyInformation } from '@static/models/company';
import { CompanyService } from '@core/site-admin/services/company.service';
import { ObservableService } from '@static/services/observable.service';
import { ToastService } from '@static/services/toast.service';
import { ProjectService } from '@shared/services/project.service';

@Component({
  selector: 'app-project-settings',
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatSelectModule,
    KeyValuePipe,
    MatCardModule,
    MatExpansionModule,
    FormsModule,
  ],
  templateUrl: './project-settings.component.html',
  styleUrl: './project-settings.component.scss',
  providers: [
    ObservableService,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class ProjectSettingsComponent implements OnInit {
  private _projectService: ProjectService = inject(ProjectService);
  private _observableService: ObservableService = inject(ObservableService);

  private _httpService: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);
  private _extensionService: ExtensionService = inject(ExtensionService);
  private _dialog: MatDialog = inject(MatDialog);
  private _authService: AuthService = inject(AuthService);
  private _companyService: CompanyService = inject(CompanyService);
  private _toastService: ToastService = inject(ToastService);

  @Input({ required: true })
  public project!: IProjectSettings;

  public projectSettingsForm!: FormGroup;
  public projExtensions!: TExtension;

  public companyList: ICompanyInformation[];

  public originalOrder = (): number => { return 0; }

  constructor(
  ) {
    // Load the company list
    this.companyList = this._companyService.companyListUpdated.value;
    this._observableService.subscribe(
      this._companyService.companyListUpdated,
      (list) => this.companyList = list
    );
    if (!this.companyList.length) {
      this._companyService.getCompanyList();
    }
  }

  ngOnInit(): void {
    this.projectSettingsForm = new FormGroup({
      updateFreq: new FormControl(this.project.update_freq),
      kpaContactEmail: new FormControl(this.project.kpa_contact_email),
      rbac: new FormControl(this.project.rbac),
      gbac: new FormControl({ value: this.project.gbac, disabled: !this.project.rbac }),
      maxHours: new FormControl(this.project.max_hours),
      managementGroups: new FormControl(this.project.management_groups),
      projectLogo: new FormControl(this.project.company_logo ? +this.project.company_logo : undefined),
    });

    this.projExtensions = this._extensionService.parseExtensionString(this.project.extensions);
  }

  /**
   * Update the project update frequency
   */
  public updateFreqChange(): void {
    const data = {
      key: 'update_freq',
      value: this.projectSettingsForm.get('updateFreq')?.value,
      updated_by: this._authService.email,
      updated_time: moment.utc().valueOf()
    };
    this._httpService.postP<void>(`${environment.backendUri}/project/${this.project.proj_id}/settings`, data)
      .then(() => {
        this.project.update_freq = data.value;
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to update project settings', err);
      });
  }

  /**
   * Update the contact email for the project
   */
  public updateContact(): void {
    const data = {
      key: 'kpa_contact_email',
      value: this.projectSettingsForm.get('kpaContactEmail')?.value,
      updated_by: this._authService.email,
      updated_time: moment.utc().valueOf()
    };
    this._httpService.postP<void>(`${environment.backendUri}/project/${this.project.proj_id}/settings`, data)
      .then(() => {
        this.project.kpa_contact_email = data.value;
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to update project settings', err);
      });
  }

  /**
   * Update the project access control toggle
   */
  public updatePermissions(permission: string): void {
    const data = {
      key: permission,
      value: this.projectSettingsForm.get(permission)?.value ? 1 : 0,
      updated_by: this._authService.email,
      updated_time: moment.utc().valueOf()
    };
    this._httpService.postP<void>(`${environment.backendUri}/project/${this.project.proj_id}/settings`, data)
      .then(() => {
        if (permission === 'rbac') {
          this.project.rbac = data.value;
          if (data.value) {
            this.projectSettingsForm.get('gbac')?.enable();
          } else {
            this.projectSettingsForm.get('gbac')?.disable();
          }
        }
        if (permission === 'gbac') this.project.gbac = data.value;
        this._projectService.updateProjectPermission(
          this.project.proj_id,
          permission as 'rbac' | 'gbac',
          data.value
        );
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to update project settings', err);
      });
  }

  /**
   * Update the project maximum working hours
   */
  public updateMaxHours(): void {
    let value = Math.trunc(+(this.projectSettingsForm.get('maxHours')?.value ?? 24));
    if (value > 24) value = 24;
    if (value < 0) value = 0;
    this.projectSettingsForm.get('maxHours')?.setValue(value);

    const data = {
      key: 'max_hours',
      value: value,
      updated_by: this._authService.email,
      updated_time: moment.utc().valueOf()
    };
    this._httpService.postP<void>(`${environment.backendUri}/project/${this.project.proj_id}/settings`, data)
      .then(() => {
        this.project.max_hours = data.value;
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to update project settings', err);
      });
  }

  /**
   * Update the management groups
   */
  public updateManagementGroups(): void {
    const values = this.projectSettingsForm.get('managementGroups')?.value as string;
    let parsedValues = '';
    if (values) {
      parsedValues = values.split(',').map(str => str.trim()).join(',');
      this.projectSettingsForm.get('managementGroups')?.setValue(parsedValues);
    }
    const data = {
      key: 'management_groups',
      value: parsedValues,
      updated_by: this._authService.email,
      updated_time: moment.utc().valueOf()
    };
    this._httpService.postP<void>(`${environment.backendUri}/project/${this.project.proj_id}/settings`, data)
      .then(() => {
        this.project.management_groups = data.value;
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to update project settings', err);
      });
  }

  /**
   * Update the project extensions
   */
  public updateExtensions(): void {
    const res = this._extensionService.updateProjectExtensions(this.projExtensions, this.project.proj_id);
    this.project.extensions = res;
  }

  /**
   * Update the company logo
   *
   * @param company company name key
   */
  public updateCompanyLogo(event: string): void {
    const data = {
      key: 'company_logo',
      value: event ?? "",
      updated_by: this._authService.email,
      updated_time: moment.utc().valueOf()
    };
    this._httpService.postP<void>(`${environment.backendUri}/project/${this.project.proj_id}/settings`, data)
      .then(() => {
        this.project.company_logo = event;
      })
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to update project settings', err);
      });
  }

  /**
   * Handle new project ID mapping
   */
  public selectNewProjectId(): void {
    // this.dialogRef.close();
    const res = this._dialog.open(NewProjectIdComponent);
    res.componentInstance.currentId = this.project.proj_id;
    res.afterClosed().subscribe((res: string | undefined) => {
      if (res === undefined || !res) return;
      const data = {
        new_id: res.toString()
      };
      const toastId = this._toastService.addToast(
        'Changing the Project ID', "Do not reload or navigate away from this page",
        true
      );
      this._httpService.postP(`${environment.backendUri}/project/${this.project.proj_id}/change`, data)
        .then(() => {
          this.project.proj_id = res.toString();
          this._snackService.openInfoSnack('Updated the project ID');
          this._projectService.loadProjects();
          this._projectService.currentId = this.project.proj_id;
          this._projectService.getCurrentInfo();
        })
        .catch((err: HttpErrorResponse) => {
          this._snackService.openErrorSnack('Failed to update project ID', err);
        })
        .finally(() => this._toastService.removeToast(toastId));
    });
  }
}
